import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
/**
 * Normalise any date value coming from the API into YYYY-MM-DD.
 * Handles: "2026-09-25", "2026-09-25T00:00:00.000Z",
 *          and verbose Date.toString() like "Fri Sep 25 2026 00:00:00 GMT-0500".
 */
function normaliseDateValue(raw) {
    // Already YYYY-MM-DD
    const isoMatch = raw.match(/^\d{4}-\d{2}-\d{2}/);
    if (isoMatch)
        return isoMatch[0];
    // Verbose Date.toString() — parse with new Date()
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
    return raw;
}
/** Format a date string into a short human-readable display (e.g. "Sep 25, 2026") */
function formatDisplayDate(raw) {
    const iso = normaliseDateValue(raw);
    const parts = iso.split('-');
    if (parts.length !== 3)
        return raw;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(d.getTime()))
        return raw;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
export function InlineEdit({ value, onSave, type = 'text', options, className = '', placeholder, disabled, hasDraft, draftStatus, onTabNext, onTabPrev }) {
    // For date fields, normalise the value to YYYY-MM-DD for <input type="date">
    const normalisedValue = value && type === 'date' ? normaliseDateValue(value) : value;
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(normalisedValue);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);
    const selectRef = useRef(null);
    useEffect(() => {
        setEditValue(normalisedValue);
    }, [normalisedValue]);
    useEffect(() => {
        if (editing) {
            if (type === 'select' && selectRef.current) {
                selectRef.current.focus();
            }
            else if (inputRef.current) {
                inputRef.current.focus();
                if (type === 'text') {
                    inputRef.current.select();
                }
            }
        }
    }, [editing, type]);
    const handleSave = useCallback(async () => {
        if (editValue === normalisedValue) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(editValue);
            setEditing(false);
        }
        catch {
            // Revert on error
            setEditValue(normalisedValue);
            setEditing(false);
        }
        finally {
            setSaving(false);
        }
    }, [editValue, normalisedValue, onSave]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            void handleSave();
        }
        else if (e.key === 'Escape') {
            setEditValue(normalisedValue);
            setEditing(false);
        }
        else if (e.key === 'Tab') {
            e.preventDefault();
            void handleSave().then(() => {
                if (e.shiftKey) {
                    onTabPrev?.();
                }
                else {
                    onTabNext?.();
                }
            });
        }
    }, [handleSave, normalisedValue, onTabNext, onTabPrev]);
    const handleSelectChange = useCallback(async (newVal) => {
        if (newVal === normalisedValue) {
            setEditing(false);
            return;
        }
        setEditValue(newVal);
        setSaving(true);
        try {
            await onSave(newVal);
            setEditing(false);
        }
        catch {
            setEditValue(normalisedValue);
            setEditing(false);
        }
        finally {
            setSaving(false);
        }
    }, [normalisedValue, onSave]);
    const displayValue = value && type === 'date' ? formatDisplayDate(value) : value;
    // For select type, show the label instead of the raw value
    const selectDisplayValue = type === 'select' && options
        ? options.find(o => o.value === value)?.label ?? value
        : displayValue;
    if (disabled) {
        return _jsx("span", { className: className, children: (type === 'select' ? selectDisplayValue : displayValue) || placeholder || '-' });
    }
    if (editing && type === 'select') {
        return (_jsx("select", { ref: selectRef, value: editValue, onChange: e => void handleSelectChange(e.target.value), onBlur: () => setEditing(false), onKeyDown: handleKeyDown, disabled: saving, className: `bg-white border border-primary-400 rounded px-1 py-0.5 text-xs outline-none focus:ring-1 focus:ring-primary-500 w-full ${saving ? 'opacity-50' : ''} ${className}`, children: options?.map(opt => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) }));
    }
    if (editing) {
        return (_jsx("input", { ref: inputRef, type: type, value: editValue, onChange: e => setEditValue(e.target.value), onBlur: () => void handleSave(), onKeyDown: handleKeyDown, disabled: saving, className: `bg-white border border-primary-400 rounded px-1.5 py-0.5 text-sm outline-none focus:ring-1 focus:ring-primary-500 w-full ${saving ? 'opacity-50' : ''} ${className}` }));
    }
    return (_jsxs("span", { onDoubleClick: () => setEditing(true), className: `cursor-text hover:bg-surface-muted rounded px-1 py-0.5 block min-w-[2rem] relative ${className}`, title: "Double-click to edit", children: [(type === 'select' ? selectDisplayValue : displayValue) || _jsx("span", { className: "text-text-tertiary", children: placeholder || '-' }), hasDraft && (_jsx("span", { className: `absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${draftStatus === 'submitted' ? 'bg-amber-500' : 'bg-primary-500'}`, title: draftStatus === 'submitted' ? 'Submitted for approval' : 'Unsaved draft change' }))] }));
}
//# sourceMappingURL=InlineEdit.js.map